import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { manageDisableScrolling } from '../ducks/ui.duck';
import HelpFlyout from '../components/HelpFlyout/HelpFlyout';
import HelpFlyoutFloatingButton from '../components/HelpFlyout/HelpFlyoutFloatingButton';

const HelpFlyoutContext = createContext(null);

/**
 * App-wide help flyout state. Call openHelp(contentId) from any page;
 * closeHelp clears content and closes the panel.
 */
export const HelpFlyoutProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [contentId, setContentId] = useState(null);
  const [requestedTabId, setRequestedTabId] = useState(null);

  const openHelp = useCallback((id, options = {}) => {
    if (!id) {
      return;
    }
    setContentId(id);
    setRequestedTabId(options.tabId || null);
    setIsOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setIsOpen(false);
    setContentId(null);
    setRequestedTabId(null);
  }, []);

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) => {
      dispatch(manageDisableScrolling(componentId, disableScrolling));
    },
    [dispatch]
  );

  const value = useMemo(
    () => ({
      isOpen,
      contentId,
      openHelp,
      closeHelp,
    }),
    [isOpen, contentId, openHelp, closeHelp]
  );

  return (
    <HelpFlyoutContext.Provider value={value}>
      {children}
      <HelpFlyoutFloatingButton />
      <HelpFlyout
        isOpen={isOpen}
        contentId={contentId}
        requestedTabId={requestedTabId}
        onClose={closeHelp}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </HelpFlyoutContext.Provider>
  );
};

/**
 * Access help flyout open/close API.
 * @returns {{ isOpen: boolean, contentId: string|null, openHelp: Function, closeHelp: Function }}
 */
export const useHelpFlyout = () => {
  const context = useContext(HelpFlyoutContext);
  if (!context) {
    throw new Error('useHelpFlyout must be used within HelpFlyoutProvider');
  }
  return context;
};

export default HelpFlyoutContext;
